import {Auth, Hub} from "aws-amplify";
import {AuthState, ChallengeName} from "@aws-amplify/ui-components";
import { parse } from 'query-string';
import sigV4Client from "./sigv4Client";
import {AUTH_INVALID_CODE} from "./constants";
import InvalidMfaCodeError from "../models/errors/invalid-mfa-code-error";

/*
  Expected constant values for cognito state machine change
 */
export const AUTH_STATE_CHANGE_EVENT = 'AuthStateChange';
export const AUTH_CHANNEL = 'UI Auth';

/*
  Call Cognito signup API
 */
export const cognitoSignUp = async (username, password, attributes) => {

  const { user } = await Auth.signUp({
    username,
    password,
    attributes: attributes
  });

  /*
    Trigger auth state machine change
   */
  await Hub.dispatch(AUTH_CHANNEL, {
    event: AUTH_STATE_CHANGE_EVENT,
    message: AuthState.ConfirmSignUp,
    user,
  });

  return user
}

/*
  Call Cognito sign in API
 */
export const cognitoSignIn = async (username, password) => {
  const data = await Auth.signIn(username, password);
  console.log('Data on signin', data);
  let nextState = AuthState.SignedIn;
  /*
    If MFA is enabled set state to MFA
   */
  if (data.challengeName === ChallengeName.MFASetup) {
    nextState = AuthState.TOTPSetup;
  }
  else if (data.challengeName === ChallengeName.NewPasswordRequired) {
    nextState = AuthState.ResetPassword;
  }
  else if (data.challengeName === ChallengeName.CustomChallenge) {
    nextState = AuthState.ConfirmSignIn
    console.log('Enter MFA');
  }

  /*
    Trigger auth state machine change
   */
  await Hub.dispatch(AUTH_CHANNEL, {
    event: AUTH_STATE_CHANGE_EVENT,
    message: nextState,
    data,
  });

  return data;
}

/*
  Call Cognito confirm sign in API
 */
export const cognitoConfirmSignIn = async (user, otp) => {
  const data = await Auth.sendCustomChallengeAnswer(user, otp);
  console.log('Data on confirm', data);
  if (data.challengeName === ChallengeName.CustomChallenge) {
    throw new InvalidMfaCodeError(AUTH_INVALID_CODE);
  }

  let nextState = AuthState.SignedIn;

  /*
    Trigger auth state machine change
   */
  await Hub.dispatch(AUTH_CHANNEL, {
    event: AUTH_STATE_CHANGE_EVENT,
    message: nextState,
    data,
  });

  return data;
}

/*
  Call Cognito sign out API
 */
export const cognitoSignOut = async () => {
  const nextState = AuthState.SignedOut;
  await Auth.signOut();

  /*
    Trigger auth state machine change
   */
  await Hub.dispatch(AUTH_CHANNEL, {
    event: AUTH_STATE_CHANGE_EVENT,
    message: nextState
  });
}

/*
  Call Cognito send verification code API
 */
export const cognitoSendCode = async (username) => {
  const data = await Auth.forgotPassword(username);
  return data;
}

/*
  Call Cognito forgot password API
 */
export const cognitoForgotPassword = async (username, code, password) => {
  await Auth.forgotPasswordSubmit(username, code, password);
}

/*
  Call Cognito Hub for auth state changes
 */
export const changeAuthState = async (nextState, data) => {
  /*
    Trigger auth state machine change
   */
  await Hub.dispatch(AUTH_CHANNEL, {
    event: AUTH_STATE_CHANGE_EVENT,
    message: nextState,
    data,
  });
}

/*
  Authenticate public user session
*/
export const authenticatePublicSession = (sessionData) => {
  const creds = parse(atob(sessionData));
  console.log(atob(sessionData));
  console.log(creds);


  const tokenPrefix = 'DigitalPortalPublicUser'
  const referenceNum = atob(creds['sessionId']).split(':')[1];

  localStorage.setItem(tokenPrefix+'SessionId', creds['sessionId'].replaceAll(' ','+'))
  localStorage.setItem(tokenPrefix+'AccessId', creds['accessId'].replaceAll(' ','+'))
  localStorage.setItem(tokenPrefix+'SecretKey', creds['secretKey'].replaceAll(' ','+'))
  localStorage.setItem(tokenPrefix+'SessionToken', creds['sessionToken'].replaceAll(' ','+'))
  localStorage.setItem(tokenPrefix+'TransactionId', referenceNum)
  localStorage.setItem('PUBLIC_SESSION', 'true');
}

/*
  Retrieve public user credentials
*/
export const currentPublicSessionCredentials = () => {
  const tokenPrefix = 'DigitalPortalPublicUser'
  return {
    accessKeyId: localStorage.getItem(tokenPrefix+'AccessId'),
    secretAccessKey: localStorage.getItem(tokenPrefix+'SecretKey'),
    sessionToken: localStorage.getItem(tokenPrefix+'SessionToken')
  }
}

/*
  Retrieve public user session ID
*/
export const getSessionId = () => {
  const tokenPrefix = 'DigitalPortalPublicUser'
  return localStorage.getItem(tokenPrefix+'SessionId');
}

/*
  Get Transaction Id
*/
export const getTransactionId = () => {
  const tokenPrefix = 'DigitalPortalPublicUser'
  return localStorage.getItem(tokenPrefix+'TransactionId');
}

/*
  Custom SIG4 signer
 */
export const sign = (credentials, method, endpoint, path, queryParams=null, body=null, headers={}) => {
  const signedRequest = sigV4Client.newClient({
    'accessKey' : credentials.accessKeyId,
    'secretKey' : credentials.secretAccessKey,
    'sessionToken' : credentials.sessionToken,
    'region' : 'ap-southeast-1',
    'endpoint' : endpoint
  }).signRequest({method, path, headers, queryParams, body});
  return signedRequest.headers;
}

/*
  Retrieve role from user object
 */
export const getAuthenticatedRole = (user) => {
  const roles = user.signInUserSession.idToken.payload['cognito:groups'];
  return roles ? roles[0] : '';
}

/*
  Retrieve id token
 */
export const getAuthenticatedIdToken = async () => {
 return (await Auth.currentSession()).getIdToken().getJwtToken();
}


